import React from 'react';
import { Navbar, Nav, NavDropdown, Container, Button } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullseye, faKey, faMoon, faSun } from '@fortawesome/free-solid-svg-icons';
import { TOOLS, TOOLS_ROUTE } from '../content/tools';
import { toolIcon } from '../content/toolIcons';

interface NavigationProps {
  darkMode: boolean;
  onToggleDarkMode: () => void;
}

const Navigation: React.FC<NavigationProps> = ({ darkMode, onToggleDarkMode }) => {
  const location = useLocation();
  const themeActionLabel = darkMode ? 'Switch to light theme' : 'Switch to dark theme';
  const onToolPage = TOOLS.some((tool) => tool.path === location.pathname);

  return (
    <Navbar expand="lg" className="app-navbar">
      <Container>
        <Navbar.Brand as={Link} to="/" className="app-brand">
          <span className="app-brand__mark" aria-hidden="true">
            <FontAwesomeIcon icon={faBullseye} />
          </span>
          <span className="app-brand__text">What Am I Sending</span>
        </Navbar.Brand>

        {/* Theme toggle lives in the navbar so it can never overlap the
            hamburger toggle, and stays reachable at every breakpoint. */}
        <div className="navbar-actions order-lg-last">
          <Button
            variant="outline-secondary"
            className="theme-toggle"
            onClick={onToggleDarkMode}
            aria-label={themeActionLabel}
            title={themeActionLabel}
          >
            <FontAwesomeIcon
              icon={darkMode ? faSun : faMoon}
              className="theme-toggle-icon"
              aria-hidden="true"
            />
            <span>{darkMode ? 'Light' : 'Dark'}</span>
          </Button>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
        </div>

        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="app-nav ms-auto">
            {/* The product is one link. Nine tools no longer fit as a flat run,
                so they live in a menu that describes each one rather than a row
                of bare labels that wrapped the bar onto a second line. */}
            <Nav.Link
              as={Link}
              to="/"
              active={location.pathname === '/' || location.pathname.startsWith('/endpoint')}
            >
              <FontAwesomeIcon icon={faKey} className="me-2" />
              Endpoints
            </Nav.Link>

            <NavDropdown
              title="Tools"
              id="tools-menu"
              align="end"
              className={`nav-tools${onToolPage ? ' nav-tools--active' : ''}`}
              menuVariant={darkMode ? 'dark' : undefined}
            >
              <div className="tools-menu" role="group" aria-label="Encoding and hashing tools">
                {TOOLS.map((tool) => (
                  <NavDropdown.Item
                    key={tool.slug}
                    as={Link}
                    to={tool.path}
                    className="tools-menu__item"
                    active={location.pathname === tool.path}
                  >
                    <FontAwesomeIcon
                      icon={toolIcon(tool.slug)}
                      className="tools-menu__icon"
                      aria-hidden="true"
                    />
                    <span className="tools-menu__text">
                      <span className="tools-menu__name">{tool.navLabel}</span>
                      <span className="tools-menu__summary">{tool.shortDescription}</span>
                    </span>
                  </NavDropdown.Item>
                ))}
              </div>
              <NavDropdown.Divider />
              <NavDropdown.Item as={Link} to={TOOLS_ROUTE.path} className="tools-menu__all">
                Browse all developer tools
              </NavDropdown.Item>
            </NavDropdown>

            {/* The index of every tool: a single scannable page that the
                per-tool pages and the site footer both link back to. */}
            <Nav.Link
              as={Link}
              to={TOOLS_ROUTE.path}
              active={location.pathname === TOOLS_ROUTE.path}
            >
              All tools
            </Nav.Link>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default Navigation;
